import { useMemo } from "react";
import { useAuth } from "../context/auth-context";
import { useTenantContext } from "../context/tenant-context";

/**
 * Checks permissions to write for specific admin user
 */
const usePermissions = () => {
  const { identity } = useAuth();
  const { activeTenant } = useTenantContext();
  const effectivePermissions = useMemo(() => {
    if (!identity) {
      return [];
    }

    const tenantPermissions = (activeTenant && identity?.tenantInfo[activeTenant]?.permissions) || [];

    return Array.from(new Set([...identity.permissions, ...tenantPermissions]));
  }, [identity, activeTenant]);

  const canAddUsers = effectivePermissions.includes("organisationuser:write-all");
  const canCreateCourses = effectivePermissions.includes("course:write");
  const canManageTenantCourses = effectivePermissions.includes("course:write-tenant");
  const canManagePlatformCourses = effectivePermissions.includes("course:write-all");
  const canModerate = effectivePermissions.includes("moderation:write");
  const canImpersonate = effectivePermissions.includes("platformuser:impersonate");
  const canCreateImpersonatedSession = effectivePermissions.includes("session:impersonate");
  const canManageMHFAStatus = effectivePermissions.includes("mhfa:admin");
  const canReadStats = effectivePermissions.includes("stat:read");
  const canManagePlatformPosts = effectivePermissions.includes("post:write-platform");
  const canCreateTenants = effectivePermissions.includes("tenant:admin-write");
  const canDestroyTenants = effectivePermissions.includes("tenant:admin-destroy");
  const isTenantAdmin = effectivePermissions.includes("tenant:write");
  const canViewAllTenants = effectivePermissions.includes("tenant:admin-read");
  const canDeleteAllPosts = effectivePermissions.includes("post:delete-all");
  const canReadAnonymousConfig = effectivePermissions.includes("anonymous:read-config");
  const canManageAssignmentRules = effectivePermissions.includes("assignmentrule:write");
  const canManagePlatformAutomation = effectivePermissions.includes("automation:write-platform");
  const canUpdateTranslations = effectivePermissions.includes("translation:write");
  const canGetPublishedCourses = effectivePermissions.includes("course:read-tenant");
  const canManageAutomation = effectivePermissions.includes("automation:write");
  const canManageReviews = effectivePermissions.includes("reviewdef:write");
  const canManageReviewInstances = effectivePermissions.includes("revinst:write-all");
  const canCreateAutomationInstances = effectivePermissions.includes("autoinst:write");
  const canManageAllAutomationInstances = effectivePermissions.includes("autoinst:write-all");
  const canAccessSupportTools = effectivePermissions.includes("support:admin");
  const canCreatePlatformGroups = effectivePermissions.includes("group:write-platform");
  const canManageAllPosts = effectivePermissions.includes("post:write-admin");
  const canReportPosts = effectivePermissions.includes("post:report");
  const canManageCalendarEvents = effectivePermissions.includes("calevent:write-all");
  const canManageReviewSchedules = effectivePermissions.includes("revschd:write");
  const canAdministerDepartments = effectivePermissions.includes("department:write");
  const canManageCourseInstances = effectivePermissions.includes("courseinst:write-all");

  return {
    canAddUsers,
    canModerate,
    canImpersonate,
    effectivePermissions,
    canManageMHFAStatus,
    canCreateImpersonatedSession,
    canCreateCourses,
    canDestroyTenants,
    canManageTenantCourses,
    canManagePlatformCourses,
    canReadStats,
    canManagePlatformPosts,
    canCreateTenants,
    isTenantAdmin,
    canViewAllTenants,
    canAdministerDepartments,
    canManageCourseInstances,
    canDeleteAllPosts,
    canReadAnonymousConfig,
    canManageAssignmentRules,
    canManagePlatformAutomation,
    canUpdateTranslations,
    canGetPublishedCourses,
    canManageAutomation,
    canManageReviews,
    canManageReviewInstances,
    canCreateAutomationInstances,
    canManageAllAutomationInstances,
    canAccessSupportTools,
    canCreatePlatformGroups,
    canManageAllPosts,
    canReportPosts,
    canManageCalendarEvents,
    canManageReviewSchedules
  };
};

export default usePermissions;
